import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
/*
 * QUILL'S OWN STYLESHEET IS THE CONSUMING APP'S JOB, NOT THIS FILE'S.
 *
 * This package has no node_modules of its own (see `index.ts`), so a bare
 * `import 'react-quill-new/dist/quill.snow.css'` here cannot be resolved by a
 * consumer that has not aliased it — and it broke platform-app's dev server
 * outright, on a screen that has nothing to do with legal documents.
 *
 * The app that renders this component imports `quill.snow.css` itself.
 */
import type { DeltaDocument, MergeFieldDef, NumberingStyle } from '@platform/shared-types';
import {
  DELTA_FORMATS, registerDeltaBlots, setMergeFieldLabelLookup, setTableApplyHook,
} from './deltaBlots';
import { ensureDeltaEditorStyles } from './deltaEditorStyles';
import { deltaOutline, inlineCalloutEmbeds } from './deltaOutline';
import type { OutlineHeading } from './deltaOutline';
import {
  detectTrigger, rankBlockInserts, rankHeadings, rankMergeFields, SIGN_ROLE_FOR,
  BLOCK_INSERTS,
} from './deltaSuggest';
import type { BlockInsertDef, TriggerKind } from './deltaSuggest';
import { MERGE_FIELD_GROUPS, safeMergeFieldsByGroup } from './MergeFieldPalette';
import { scheduleColumnChoices as scheduleColumnChoicesRaw } from '@platform/shared-types';

/**
 * The signature blocks, DERIVED from the `/` menu's own list rather than
 * re-typed. A hand-written copy is how the tag rail ended up missing five
 * groups: two lists of the same thing drift, and the one nobody edits is the
 * one the author is looking at.
 *
 * A NAMED FUNCTION, not a module-scope `BLOCK_INSERTS.filter(...)`. React
 * Refresh rewrites a bare arrow at module scope as `_c = (b) => …` and
 * registers it as a component; that corrupts this file's refresh boundary, and
 * the symptom is the editor going half-dead after an HMR update — the `/` menu
 * stops responding for reasons nothing in the `/` code explains.
 */
function signatureInserts(): BlockInsertDef[] {
  return BLOCK_INSERTS.filter((b) => b.id.startsWith('sign'));
}

/**
 * Guarded runtime import (project rule). A stale Vite pre-bundle resolves a new
 * export to `undefined`, and calling it would blank the schedule builder with
 * no explanation. An empty column list renders the "catalog did not load"
 * notice instead, and the rest of the editor keeps working.
 */
function safeScheduleColumns(source: 'equipment' | 'packages'): { key: string; label: string }[] {
  if (typeof scheduleColumnChoicesRaw !== 'function') {
    // eslint-disable-next-line no-console
    console.error(
      '@platform/shared-types exported no scheduleColumnChoices — a stale Vite pre-bundle. '
      + 'RESTART the dev server: deleting node_modules/.vite under a running server does nothing.',
    );
    return [];
  }
  try {
    return scheduleColumnChoicesRaw(source, safeMergeFieldsByGroup() as any);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[DeltaEditor] scheduleColumnChoices() threw:', e);
    return [];
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * THE CONTINUOUS DOCUMENT EDITOR.
 *
 * One Quill instance over the WHOLE document, not one per block. That is the
 * entire point: the previous editor made an author pick a block type from a
 * menu, fill one input, then pick the next, and editing a contract felt like
 * completing a form.
 *
 * Quill is the INPUT DEVICE. The stored value is a `DeltaDocument` — Quill's
 * own `getContents()` shape, validated by a `.strict()` schema before it is
 * saved. Quill's HTML is never stored, so a Quill upgrade (or replacing Quill
 * entirely) changes no stored document.
 *
 * PURE PRESENTATIONAL, like everything else in this package: no fetch, no
 * client, no router. The delta comes in as a prop and every change goes out
 * through `onChange`; the host decides what to persist and when.
 *
 * See `deltaBlots.ts` for why merge fields are embeds rather than text, and
 * `deltaSuggest.ts` for the `@` / `/` / `#` rules.
 */

/* ── the closed toolbar ─────────────────────────────────────────────────── */

const HEADER_CHOICES = [
  { level: 1, label: 'H1' },
  { level: 2, label: 'H2' },
  { level: 3, label: 'H3' },
] as const;

interface ActiveFormats {
  bold?: boolean; italic?: boolean; underline?: boolean; strike?: boolean; link?: string;
  header?: number; list?: string; align?: string; callout?: string;
}

/** The three highlight tones, matching both renderers' colours exactly. */
const CALLOUT_TONES = [
  { tone: 'info', label: 'Info', swatch: 'border-sky-400 bg-sky-100' },
  { tone: 'warning', label: 'Warning', swatch: 'border-amber-400 bg-amber-100' },
  { tone: 'danger', label: 'Danger', swatch: 'border-red-400 bg-red-100' },
] as const;

/* ── the menu ───────────────────────────────────────────────────────────── */

interface MenuState {
  kind: TriggerKind;
  query: string;
  /** Absolute Quill index of the trigger character. */
  from: number;
  /** Absolute Quill index of the caret. */
  to: number;
  top: number;
  left: number;
}

type MenuRow =
  | { key: string; kind: 'field'; def: MergeFieldDef }
  | { key: string; kind: 'block'; def: BlockInsertDef }
  | { key: string; kind: 'ref'; heading: OutlineHeading };

const MENU_ICON: Record<TriggerKind, string> = { '@': '@', '/': '/', '#': '§' };

const TRIGGER_TITLE: Record<TriggerKind, string> = {
  '@': 'Tag', '/': 'Insert', '#': 'Reference',
};

export interface DeltaEditorProps {
  /** The document being edited. The host owns it; the editor never mutates it. */
  value: DeltaDocument | null | undefined;
  onChange: (next: DeltaDocument) => void;
  readOnly?: boolean;
  /**
   * Numbering style, plumbed from the document so the editor shows a section
   * number exactly where the renderer would print one — and none where it
   * would not. Defaults to `'none'`, matching `RenderMeta.numbering`.
   */
  numbering?: NumberingStyle;
  placeholder?: string;
  className?: string;
  /**
   * Handed the editor's small imperative surface once it has mounted, so the
   * host's palette rail can drop a field at the caret.
   *
   * A callback rather than a module-level global: two editors on one page
   * would otherwise overwrite each other's handle, and the last one mounted
   * would silently receive every other one's insertions.
   */
  onApiReady?: (api: DeltaEditorApi | null) => void;
}

export interface DeltaEditorApi {
  /** Insert a merge-field chip wherever the caret is, replacing any selection. */
  insertField: (name: string) => void;
  /** Move the caret to a character index and scroll it into view. */
  goTo: (index: number) => void;
}

export function DeltaEditor({
  value, onChange, readOnly = false, numbering = 'none',
  placeholder = 'Start typing. Press @ for a tag, / to insert, # to reference a section.',
  className = '', onApiReady,
}: DeltaEditorProps) {
  const quillRef = useRef<ReactQuill | null>(null);
  /**
   * The last document this editor produced.
   *
   * THE EDITOR IS UNCONTROLLED AFTER MOUNT, and this is what makes that safe.
   *
   * Passing the Delta as ReactQuill's `value` made it a controlled component:
   * every keystroke produced a new object, which Quill took as an external
   * document and answered by rebuilding its entire DOM through `setContents`.
   * On a thirteen-section contract that is a full re-render per character —
   * the tab stops responding, and the caret jumps to the end of the document
   * on the keystrokes that do land.
   *
   * So Quill owns its content while the author types, and `value` is pushed
   * back in ONLY when it did not come from here — switching versions, or
   * flipping out of the redline view. Comparing by reference is what tells
   * those two cases apart.
   */
  const lastEmitted = useRef<DeltaDocument | null>(null);
  /** Captured once: ReactQuill reads it at mount and never again. */
  const [initialValue] = useState(
    () => (inlineCalloutEmbeds(value) ?? { ops: [{ insert: '\n' }] }) as any,
  );
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [cursor, setCursor] = useState(0);
  const [formats, setFormats] = useState<ActiveFormats>({});
  /** Rows x columns picker for the table button; null while closed. */
  const [tablePick, setTablePick] = useState<{ r: number; c: number } | null>(null);
  const [schedulePick, setSchedulePick] = useState(false);
  const [signPick, setSignPick] = useState(false);

  // Registration must happen before the first ReactQuill mount, and exactly
  // once for the whole bundle — `registerDeltaBlots` guards re-entry itself.
  useMemo(() => {
    ensureDeltaEditorStyles();
    registerDeltaBlots(Quill);
  }, []);

  /* ── the catalog, loaded through the palette's existing guard ─────────── */
  const catalog = useMemo<MergeFieldDef[]>(() => {
    const grouped = safeMergeFieldsByGroup();
    return MERGE_FIELD_GROUPS.flatMap((g) => grouped[g] ?? []);
  }, []);

  const labelOf = useMemo(() => {
    const byName = new Map(catalog.map((d) => [d.name, d.label]));
    return (name: string) => byName.get(name);
  }, [catalog]);

  // The blots draw chips as labels rather than raw tokens, and they are
  // static classes with no access to props — so the lookup is handed to them.
  useEffect(() => { setMergeFieldLabelLookup(labelOf); }, [labelOf]);

  /*
   * COMMITTING A TABLE EDIT.
   *
   * Quill caches its delta and cannot see inside a block embed, so a table
   * edit has to be replayed through Quill's own API to land in the model at
   * all. Replacing the embed in place does that: it invalidates the cache,
   * emits `text-change` (so `handleChange` reports it upward like any other
   * edit), and puts the change in the undo stack.
   *
   * The selection is restored to just after the table, because deleting the
   * embed drops the caret and leaving it at 0 would jump the author to the
   * top of the contract every time they finish a cell.
   */
  useEffect(() => {
    setTableApplyHook((node, next) => {
      const q = editor();
      if (!q) return;
      const blot = (Quill as any).find(node);
      if (!blot) return;
      const index = q.getIndex(blot);
      if (typeof index !== 'number' || index < 0) return;
      q.deleteText(index, 1, 'user');
      q.insertEmbed(index, 'table', next, 'user');
      q.setSelection(index + 1, 0, 'silent');
    });
    return () => setTableApplyHook(() => {});
  }, []);

  const headings = useMemo(() => deltaOutline(value, numbering), [value, numbering]);

  const editor = (): any => quillRef.current?.getEditor?.() ?? null;

  /* ── derived section numbers, stamped onto the DOM ────────────────────
   *
   * Quill owns the editor's DOM, so the numbers cannot be React children —
   * they are a `data-secno` attribute the stylesheet draws with `::before`,
   * re-stamped after every change. `pointer-events:none` on that pseudo
   * element keeps the number out of the text flow entirely: it is never
   * selectable, never copied, and never part of what gets stored.
   */
  useEffect(() => {
    const q = editor();
    if (!q) return;
    const nodes = q.root.querySelectorAll('h1,h2,h3');
    let i = 0;
    const numbered = headings;
    nodes.forEach((node: HTMLElement) => {
      const h = numbered[i];
      i += 1;
      if (h && h.number) node.setAttribute('data-secno', h.number);
      else node.removeAttribute('data-secno');
    });
  }, [headings, value]);

  /* ── what the menu is offering right now ──────────────────────────────── */
  const rows = useMemo<MenuRow[]>(() => {
    if (!menu) return [];
    if (menu.kind === '@') {
      return rankMergeFields(catalog, menu.query)
        .slice(0, 60)
        .map((def) => ({ key: def.name, kind: 'field' as const, def }));
    }
    if (menu.kind === '/') {
      return rankBlockInserts(menu.query).map((def) => ({ key: def.id, kind: 'block' as const, def }));
    }
    return rankHeadings(headings.filter((h) => h.level !== 1), menu.query)
      .slice(0, 40)
      .map((h, i) => ({ key: `${h.anchorId ?? h.text}-${i}`, kind: 'ref' as const, heading: h }));
  }, [menu, catalog, headings]);

  useEffect(() => { setCursor(0); }, [menu?.kind, menu?.query]);

  const closeMenu = useCallback(() => setMenu(null), []);

  /* ── trigger detection, run on every caret move and edit ──────────────── */
  const syncMenu = useCallback(() => {
    const q = editor();
    if (!q || readOnly) return;
    const sel = q.getSelection();
    if (!sel || sel.length > 0) { setMenu(null); return; }

    const [line] = q.getLine(sel.index);
    if (!line) { setMenu(null); return; }
    const lineStart = q.getIndex(line);
    const before = q.getText(lineStart, sel.index - lineStart);

    const hit = detectTrigger(before);
    if (!hit) { setMenu(null); return; }

    const from = lineStart + hit.start;
    const bounds = q.getBounds(from, 0);
    setMenu({
      kind: hit.kind,
      query: hit.query,
      from,
      to: sel.index,
      // Positioned against the editor's own container, which is the offset
      // parent of the absolutely-positioned menu.
      top: bounds.top + bounds.height + 6,
      left: Math.max(8, Math.min(bounds.left, q.root.clientWidth - 360)),
    });
  }, [readOnly]);

  const syncFormats = useCallback(() => {
    const q = editor();
    if (!q) return;
    const sel = q.getSelection();
    setFormats(sel ? (q.getFormat(sel) as ActiveFormats) : {});
  }, []);

  /* ── insertion ────────────────────────────────────────────────────────── */

  /**
   * Replace the trigger text with what the author picked, in ONE operation.
   *
   * The `@sign` the author typed is deleted before the chip is inserted —
   * skip that and the document keeps `@sign{{signer_name}}`, with the literal
   * half of it sitting in a contract forever.
   */
  const applyChoice = useCallback((row: MenuRow) => {
    const q = editor();
    if (!q || !menu) return;

    const len = menu.to - menu.from;
    q.deleteText(menu.from, len, 'user');

    if (row.kind === 'field') {
      q.insertEmbed(menu.from, 'mergeField', { name: row.def.name }, 'user');
      q.setSelection(menu.from + 1, 0, 'silent');
    } else if (row.kind === 'ref') {
      const h = row.heading;
      // A reference needs an anchor to survive the heading moving. A heading
      // that has none yet is referenced by its words alone, which the
      // renderer already treats as the fallback rendering.
      const text = h.number ? `§ ${h.number.replace(/\.$/, '')} ${h.text}` : `“${h.text}”`;
      q.insertEmbed(menu.from, 'docRef', { anchorId: h.anchorId ?? '', text }, 'user');
      q.setSelection(menu.from + 1, 0, 'silent');
    } else {
      applyBlockInsert(q, menu.from, row.def.id);
    }

    setMenu(null);
  }, [menu]);

  const applyBlockInsert = (q: any, at: number, id: BlockInsertDef['id']) => {
    switch (id) {
      case 'heading1': case 'heading2': case 'heading3': {
        const level = Number(id.slice(-1));
        q.formatLine(at, 1, 'header', level, 'user');
        q.setSelection(at, 0, 'silent');
        return;
      }
      case 'bullet': case 'ordered':
        q.formatLine(at, 1, 'list', id, 'user');
        q.setSelection(at, 0, 'silent');
        return;
      case 'table':
        // The `/` route opens the SAME size picker the toolbar does, rather
        // than quietly inserting a 2x2 an author then has to reshape.
        q.setSelection(at, 0, 'silent');
        setTablePick({ r: 0, c: 0 });
        return;
      case 'callout':
        q.insertEmbed(at, 'callout', {
          tone: 'info', content: [{ insert: 'Callout text' }],
        }, 'user');
        q.setSelection(at + 1, 0, 'silent');
        return;
      case 'signCustomer': case 'signCompany': case 'signGuarantor': case 'signWitness': {
        const { role, caption } = SIGN_ROLE_FOR[id];
        q.insertEmbed(at, 'signatureField', {
          signerRole: role,
          label: caption,
          // Printed name and date default ON: a signature without a printed
          // name is hard to attribute, and an undated one invites a dispute
          // about when it was given. Title is on for the two parties that
          // sign in a capacity — an individual guarantor signs personally.
          showName: true,
          showDate: true,
          showTitle: role === 'customer' || role === 'platform',
        }, 'user');
        q.setSelection(at + 1, 0, 'silent');
        return;
      }
      case 'pageBreak':
        q.insertEmbed(at, 'pageBreak', true, 'user');
        q.setSelection(at + 1, 0, 'silent');
        return;
      default:
        return;
    }
  };

  /**
   * Insert a table of a CHOSEN size.
   *
   * A fixed 2x2 was the wrong default: a table's shape is the first thing an
   * author knows and the most annoying thing to change afterwards, so it is
   * asked for up front the way every word processor asks.
   */
  const insertTable = useCallback((rows: number, cols: number) => {
    const q = editor();
    if (!q || readOnly) return;
    const sel = q.getSelection(true);
    const at = sel ? sel.index : q.getLength() - 1;
    q.insertEmbed(at, 'table', {
      headers: Array.from({ length: cols }, (_, i) => `Column ${i + 1}`),
      rows: Array.from({ length: rows }, () => new Array(cols).fill('')),
    }, 'user');
    q.setSelection(at + 1, 0, 'silent');
  }, [readOnly]);

  /**
   * Insert a schedule. Unlike a table there is nothing to type into it
   * afterwards — the rows come from the order — so the whole configuration is
   * settled in the dialog and committed once.
   */
  const insertSchedule = useCallback((value: {
    source: 'equipment' | 'packages';
    columns: { key: string; label: string }[];
    title?: string;
    allowEmpty?: boolean;
  }) => {
    const q = editor();
    if (!q || readOnly) return;
    const sel = q.getSelection(true);
    const at = sel ? sel.index : q.getLength() - 1;
    q.insertEmbed(at, 'scheduleTable', value, 'user');
    q.setSelection(at + 1, 0, 'silent');
  }, [readOnly]);

  /**
   * Toolbar route to a signature block — the SAME `applyBlockInsert` the `/`
   * menu calls, so the two can never insert different things.
   *
   * A toolbar button exists because `/sign` was undiscoverable: signature
   * blocks are a headline feature of a contract editor, and the only way in was
   * a keystroke nobody had been told about. Authors looked in the right rail
   * (merge fields only) and under `@` (merge fields only) and concluded the
   * feature was missing.
   */
  const insertSignature = useCallback((id: BlockInsertDef['id']) => {
    const q = editor();
    if (!q || readOnly) return;
    const sel = q.getSelection(true);
    const at = sel ? sel.index : q.getLength() - 1;
    applyBlockInsert(q, at, id);
  }, [readOnly]);

  /** Palette click / drag target: drop a field wherever the caret last was. */
  const insertFieldAtCaret = useCallback((name: string) => {
    const q = editor();
    if (!q || readOnly) return;
    const sel = q.getSelection(true);
    const at = sel ? sel.index : q.getLength() - 1;
    if (sel && sel.length) q.deleteText(at, sel.length, 'user');
    q.insertEmbed(at, 'mergeField', { name }, 'user');
    q.setSelection(at + 1, 0, 'silent');
  }, [readOnly]);

  /**
   * Push an EXTERNAL document into Quill, and only an external one.
   *
   * The reference check short-circuits the common case — the host storing what
   * we just emitted and handing it straight back — without touching the
   * editor. Only when the incoming document is genuinely a different object do
   * we pay for a structural comparison, and only when that also differs do we
   * replace the contents. The selection is restored afterwards because
   * `setContents` drops it, and losing the caret mid-edit is the symptom this
   * whole arrangement exists to avoid.
   */
  useEffect(() => {
    const q = editor();
    if (!q || !value) return;
    if (value === lastEmitted.current) return;
    // Inlined here too, not only at mount: Quill has no `callout` blot any
    // more, so an embed reaching `setContents` would be dropped on the floor
    // and the aside would vanish from the document.
    const incoming = inlineCalloutEmbeds(value) as any;
    const current = q.getContents();
    if (JSON.stringify(current.ops) === JSON.stringify(incoming.ops)) return;
    const sel = q.getSelection();
    q.setContents(incoming, 'silent');
    if (sel) {
      const max = q.getLength() - 1;
      q.setSelection(Math.min(sel.index, max), 0, 'silent');
    }
  }, [value]);

  const goTo = useCallback((index: number) => {
    const q = editor();
    if (!q) return;
    q.setSelection(index, 0, 'user');
    q.focus();
  }, []);

  // Published after mount and withdrawn on unmount, so a host holding the API
  // never calls into an editor that is no longer on the page.
  useEffect(() => {
    if (!onApiReady) return undefined;
    onApiReady({ insertField: insertFieldAtCaret, goTo });
    return () => onApiReady(null);
  }, [onApiReady, insertFieldAtCaret, goTo]);

  /* ── keyboard, while the menu is open ─────────────────────────────────
   *
   * A capture-phase listener on Quill's own root rather than a Quill keyboard
   * binding. Quill binds Enter, Tab and the arrows itself for lists and
   * indentation; competing with it through binding priority is fragile, and
   * losing that race means Enter inserts a newline instead of the field the
   * author is looking straight at.
   */
  const menuRef = useRef<MenuState | null>(null);
  const rowsRef = useRef<MenuRow[]>([]);
  const cursorRef = useRef(0);
  menuRef.current = menu;
  rowsRef.current = rows;
  cursorRef.current = cursor;

  useEffect(() => {
    const q = editor();
    if (!q) return undefined;
    const root: HTMLElement = q.root;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!menuRef.current) return;
      const list = rowsRef.current;

      if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        setMenu(null);
        return;
      }
      if (!list.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault(); e.stopPropagation();
        setCursor((c) => (c + 1) % list.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault(); e.stopPropagation();
        setCursor((c) => (c - 1 + list.length) % list.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault(); e.stopPropagation();
        applyChoice(list[Math.min(cursorRef.current, list.length - 1)]);
      }
    };

    root.addEventListener('keydown', onKeyDown, true);
    return () => root.removeEventListener('keydown', onKeyDown, true);
  }, [applyChoice]);

  /* ── Quill config ─────────────────────────────────────────────────────── */

  const modules = useMemo(() => ({
    // Our own toolbar below — Quill's would offer formats the schema rejects.
    toolbar: false,
    clipboard: {
      // Paste is reduced to the closed format set. Without this an author can
      // paste a coloured, 18pt, block-quoted clause out of Word, see it in
      // the editor, and watch it vanish from the executed PDF.
      matchVisual: false,
    },
    history: { userOnly: true },
  }), []);

  const handleChange = useCallback((_html: string, _delta: any, source: string) => {
    const q = editor();
    if (!q) return;
    syncFormats();
    // (see `lastEmitted` below — the echo guard lives in handleChange too)
    /*
     * ONLY A USER EDIT COUNTS AS A CHANGE.
     *
     * Quill emits an `api`-sourced change when it first takes the document in,
     * and again whenever it normalises one. Reporting those upward marked an
     * untouched draft as unsaved the moment it opened — which trains an author
     * to ignore the unsaved-changes warning, on the one screen where ignoring
     * it means submitting a document the reviewer will not see.
     *
     * The editor's own insertions (`insertEmbed`, `formatLine`) pass 'user'
     * deliberately, so a field dropped from the palette still counts.
     */
    if (source !== 'user') return;
    syncMenu();
    const next = q.getContents() as unknown as DeltaDocument;
    // Remembered BEFORE it goes up, so when the host stores it and hands the
    // very same object back as `value`, the sync effect below recognises it as
    // our own echo and leaves Quill alone.
    lastEmitted.current = next;
    onChange(next);
  }, [onChange, syncMenu, syncFormats]);

  const handleSelection = useCallback(() => { syncMenu(); syncFormats(); }, [syncMenu, syncFormats]);

  /* ── toolbar actions ──────────────────────────────────────────────────── */
  const toggle = (name: string, on?: any) => {
    const q = editor();
    if (!q) return;
    q.focus();
    const sel = q.getSelection();
    if (!sel) return;
    const current = (q.getFormat(sel) as any)[name];
    q.format(name, on !== undefined ? (current === on ? false : on) : !current, 'user');
    syncFormats();
  };

  const TB = ({ label, active, onClick, title }: {
    label: any; active?: boolean; onClick: () => void; title: string;
  }) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={!!active}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`flex h-[29px] min-w-[29px] items-center justify-center rounded-md px-1.5 text-[13px] transition-colors ${
        active ? 'bg-rose-50 text-rose-800' : 'text-stone-600 hover:bg-stone-100'
      }`}
    >
      {label}
    </button>
  );

  const Sep = () => <span className="mx-1 h-[18px] w-px bg-stone-200" aria-hidden="true" />;

  return (
    <div className={`relative ${className}`}>
      {!readOnly && (
        // FULLY opaque, and above the page. The document scrolls underneath
        // this bar, so any transparency at all shows contract text through the
        // toolbar buttons.
        <div className="sticky top-0 z-30 mx-auto mb-4 flex w-[min(760px,100%)] flex-wrap items-center gap-0.5 rounded-lg border border-stone-200 bg-white px-1.5 py-1 shadow-md">
          <TB title="Bold (⌘B)" label={<b className="font-extrabold">B</b>} active={formats.bold} onClick={() => toggle('bold')} />
          <TB title="Italic (⌘I)" label={<i className="font-semibold italic">I</i>} active={formats.italic} onClick={() => toggle('italic')} />
          <TB title="Underline (⌘U)" label={<u>U</u>} active={formats.underline} onClick={() => toggle('underline')} />
          <TB
            title="Strikethrough (⌘⇧X)"
            label={<s>S</s>}
            active={formats.strike}
            onClick={() => toggle('strike')}
          />
          <TB
            title="Link (⌘K)"
            label="🔗"
            active={!!formats.link}
            onClick={() => {
              const q = editor();
              if (!q) return;
              const sel = q.getSelection();
              if (!sel || !sel.length) return;
              if (formats.link) { q.format('link', false, 'user'); syncFormats(); return; }
              const href = window.prompt('Link URL (http or https)');
              if (!href) return;
              // The schema accepts http(s) only — a `javascript:` href must
              // never reach a document somebody signs. Rejected here so the
              // author is told, rather than at save time on the whole file.
              if (!/^https?:\/\//i.test(href)) {
                window.alert('Links must start with http:// or https://');
                return;
              }
              q.format('link', href, 'user');
              syncFormats();
            }}
          />
          <Sep />
          {HEADER_CHOICES.map((h) => (
            <TB
              key={h.level}
              title={`Heading ${h.level}`}
              label={h.label}
              active={formats.header === h.level}
              onClick={() => toggle('header', h.level)}
            />
          ))}
          <TB title="Body text" label="¶" active={!formats.header} onClick={() => toggle('header', false as any)} />
          <Sep />
          <TB title="Bulleted list" label="• List" active={formats.list === 'bullet'} onClick={() => toggle('list', 'bullet')} />
          <TB title="Numbered list" label="1. List" active={formats.list === 'ordered'} onClick={() => toggle('list', 'ordered')} />
          <Sep />
          {/* Highlight: the aside an author reaches for most. Three tones, each
              a toggle — clicking the active one turns the highlight off. */}
          {CALLOUT_TONES.map((t) => (
            <TB
              key={t.tone}
              title={`${t.label} highlight`}
              label={<span className={`inline-block h-3.5 w-3.5 rounded-sm border ${t.swatch}`} />}
              active={formats.callout === t.tone}
              onClick={() => toggle('callout', t.tone)}
            />
          ))}
          <Sep />
          <span className="relative">
            <TB
              title="Insert table"
              label="▦"
              active={!!tablePick}
              onClick={() => setTablePick((p) => (p ? null : { r: 0, c: 0 }))}
            />
            {tablePick && (
              <TableSizePicker
                onPick={(rows, cols) => { setTablePick(null); insertTable(rows, cols); }}
                onClose={() => setTablePick(null)}
              />
            )}
          </span>
          <span className="relative">
            <TB
              title="Insert schedule — one row per device or package on the order"
              label="▤+"
              active={schedulePick}
              onClick={() => setSchedulePick((p) => !p)}
            />
            {schedulePick && (
              <SchedulePicker
                onPick={(v) => { setSchedulePick(false); insertSchedule(v); }}
                onClose={() => setSchedulePick(false)}
              />
            )}
          </span>
          <span className="relative">
            <TB
              title="Insert a signature block"
              label="✍"
              active={signPick}
              onClick={() => setSignPick((p) => !p)}
            />
            {signPick && (
              <div className="absolute left-0 top-[calc(100%+6px)] z-40 w-56 rounded-lg border border-stone-300 bg-white p-1 shadow-xl">
                {signatureInserts().map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setSignPick(false); insertSignature(b.id); }}
                    className="block w-full rounded-md px-2.5 py-1.5 text-left text-[12.5px] text-stone-700 hover:bg-stone-100"
                  >
                    {b.label.replace('Signature — ', '')}
                    {b.hint && <span className="block text-[10.5px] text-stone-400">{b.hint}</span>}
                  </button>
                ))}
              </div>
            )}
          </span>
          <Sep />
          <TB title="Align left" label="≡" active={!formats.align} onClick={() => toggle('align', false as any)} />
          <TB title="Align centre" label="≣" active={formats.align === 'center'} onClick={() => toggle('align', 'center')} />
          <Sep />
          <span className="whitespace-nowrap px-1.5 text-[11.5px] text-stone-400">
            <Kbd>@</Kbd> tag <Kbd>/</Kbd> insert <Kbd>#</Kbd> reference
          </span>
        </div>
      )}

      <div className="dq-page relative">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          readOnly={readOnly}
          // UNCONTROLLED — see `lastEmitted`. A `value` prop here would make
          // Quill rebuild the whole document on every keystroke.
          defaultValue={initialValue}
          formats={DELTA_FORMATS as string[]}
          modules={modules}
          placeholder={placeholder}
          onChange={handleChange}
          onChangeSelection={handleSelection}
        />

        {menu && (
          <div className="dq-menu" style={{ top: menu.top, left: menu.left }} role="listbox">
            <div className="dq-menu-head">
              <span className="q">
                {TRIGGER_TITLE[menu.kind]} · <b>{menu.kind}{menu.query}</b>
              </span>
              <span className="ct">
                {menu.kind === '@' ? `${rows.length} of ${catalog.length}` : rows.length}
              </span>
            </div>
            <div className="dq-menu-list">
              {rows.length === 0 && (
                <div className="dq-menu-empty">
                  Nothing matches “{menu.query}”. <b>Esc</b> keeps it as plain text.
                </div>
              )}
              {rows.map((row, i) => (
                <div
                  key={row.key}
                  role="option"
                  aria-selected={i === cursor}
                  className={`dq-row ${i === cursor ? 'on' : ''}`}
                  onMouseEnter={() => setCursor(i)}
                  onMouseDown={(e) => { e.preventDefault(); applyChoice(row); }}
                >
                  <span className="ico">{MENU_ICON[menu.kind]}</span>
                  {row.kind === 'field' && (
                    <>
                      <span className="lab">{row.def.label}</span>
                      <span className="ex">“{row.def.example}”</span>
                    </>
                  )}
                  {row.kind === 'block' && (
                    <>
                      <span className="lab">{row.def.label}</span>
                      {row.def.hint && <span className="ex">{row.def.hint}</span>}
                    </>
                  )}
                  {row.kind === 'ref' && (
                    <span className="lab">
                      {row.heading.number ? `${row.heading.number.replace(/\.$/, '')} · ` : ''}
                      {row.heading.text}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="dq-menu-foot">
              <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
              <span><kbd>↵</kbd> insert</span>
              <span><kbd>esc</kbd> keep as text</span>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

/**
 * The rows × columns grid every word processor uses.
 *
 * Hovering sizes the table, clicking inserts it. 8 × 8 covers every table in
 * the corpus (the widest is 5 columns) without becoming a wall; a bigger table
 * is built by adding rows with the table's own controls, which is where an
 * author is looking by then anyway.
 */
function TableSizePicker({
  onPick, onClose,
}: { onPick: (rows: number, cols: number) => void; onClose: () => void }) {
  const [hover, setHover] = useState<{ r: number; c: number }>({ r: 1, c: 2 });
  const MAX_R = 8;
  const MAX_C = 8;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="absolute left-0 top-[calc(100%+6px)] z-40 rounded-lg border border-stone-300 bg-white p-2 shadow-xl"
      onMouseLeave={() => setHover({ r: 0, c: 0 })}
    >
      <div className="mb-1.5 text-center text-[11px] font-semibold text-stone-600">
        {hover.r > 0 ? `${hover.r} × ${hover.c}` : 'Pick a size'}
      </div>
      <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${MAX_C}, 16px)` }}>
        {Array.from({ length: MAX_R * MAX_C }, (_, i) => {
          const r = Math.floor(i / MAX_C) + 1;
          const c = (i % MAX_C) + 1;
          const on = r <= hover.r && c <= hover.c;
          return (
            <button
              key={i}
              type="button"
              aria-label={`${r} by ${c}`}
              onMouseEnter={() => setHover({ r, c })}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(r, c)}
              className={`h-4 w-4 rounded-[2px] border ${
                on ? 'border-rose-500 bg-rose-200' : 'border-stone-300 bg-stone-50'
              }`}
            />
          );
        })}
      </div>
      <div className="mt-1.5 text-center text-[10px] text-stone-400">
        Header row included · add more later
      </div>
    </div>
  );
}

/**
 * THE SCHEDULE BUILDER.
 *
 * Two decisions and a name: which source the rows come from, and which of that
 * source's fields to show — all of them or a few, in the order ticked. The
 * column choices come from the merge-field catalog (`scheduleColumnChoices`),
 * so a field added to the `device` or `package` group becomes available here
 * with no second edit, and a column can never name a cell the row source does
 * not produce.
 */
function SchedulePicker({
  onPick, onClose,
}: {
  onPick: (v: { source: 'equipment' | 'packages'; columns: { key: string; label: string }[]; title?: string; allowEmpty?: boolean }) => void;
  onClose: () => void;
}) {
  const [source, setSource] = useState<'equipment' | 'packages'>('equipment');
  const [picked, setPicked] = useState<string[]>(['model', 'quantity', 'unit_price']);
  const [title, setTitle] = useState('Schedule A — Equipment');
  const [allowEmpty, setAllowEmpty] = useState(false);

  const choices = useMemo(() => safeScheduleColumns(source), [source]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Switching source invalidates the column choice — the two key spaces do not
  // overlap, and carrying `unit_price` onto a package schedule would build a
  // column of em dashes.
  const changeSource = (next: 'equipment' | 'packages') => {
    setSource(next);
    setPicked(next === 'equipment' ? ['model', 'quantity', 'unit_price'] : ['name', 'monthly_price']);
    setTitle(next === 'equipment' ? 'Schedule A — Equipment' : 'Schedule B — Software');
  };

  const toggleColumn = (key: string) => setPicked((prev) => (
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  ));

  // Ordered by the author's ticking order, which is the order they appear.
  const columns = picked
    .map((key) => choices.find((c) => c.key === key))
    .filter((c): c is { key: string; label: string } => !!c);

  return (
    <div
      className="absolute left-0 top-[calc(100%+6px)] z-40 w-[360px] rounded-lg border border-stone-300 bg-white p-3 shadow-xl"
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
        Insert schedule
      </div>

      <div className="mb-2.5 flex gap-1.5">
        {(['equipment', 'packages'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => changeSource(s)}
            className={`min-h-[32px] flex-1 rounded-md border px-2 text-[12px] font-medium ${
              source === s ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-stone-300 text-stone-600'
            }`}
          >
            {s === 'equipment' ? 'Devices' : 'Packages'}
          </button>
        ))}
      </div>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (optional)"
        className="mb-2.5 w-full rounded-md border border-stone-300 px-2 py-1.5 text-[12.5px] text-stone-800"
      />

      <div className="mb-1 text-[11px] text-stone-500">Columns — tick in the order they should appear</div>
      <div className="mb-2.5 max-h-[168px] overflow-y-auto rounded-md border border-stone-200 p-1.5">
        {choices.length === 0 && (
          <div className="px-1 py-2 text-[11.5px] text-stone-400">
            No columns available — the merge-field catalog did not load.
          </div>
        )}
        {choices.map((c) => {
          const at = picked.indexOf(c.key);
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => toggleColumn(c.key)}
              className={`flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[12px] ${
                at >= 0 ? 'text-stone-800' : 'text-stone-500 hover:bg-stone-50'
              }`}
            >
              <span className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border text-[9px] font-bold ${
                at >= 0 ? 'border-rose-400 bg-rose-100 text-rose-700' : 'border-stone-300'
              }`}
              >
                {at >= 0 ? at + 1 : ''}
              </span>
              {c.label}
            </button>
          );
        })}
      </div>

      <label className="mb-2.5 flex items-center gap-2 text-[11.5px] text-stone-600">
        <input type="checkbox" checked={allowEmpty} onChange={(e) => setAllowEmpty(e.target.checked)} />
        Allow this schedule to render with no rows
      </label>
      {!allowEmpty && (
        <p className="mb-2.5 text-[11px] text-stone-400">
          An agreement whose schedule has no rows will refuse to render — the empty table is a
          defect, not a default.
        </p>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="min-h-[32px] rounded-md border border-stone-300 px-3 text-[12px] text-stone-600">
          Cancel
        </button>
        <button
          type="button"
          disabled={columns.length === 0}
          onClick={() => onPick({
            source,
            columns,
            title: title.trim() || undefined,
            allowEmpty: allowEmpty || undefined,
          })}
          className="min-h-[32px] rounded-md px-3 text-[12px] font-semibold text-white disabled:opacity-50"
          style={{ background: '#9f1239' }}
        >
          Insert
        </button>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: any }) {
  return (
    <kbd className="rounded border border-b-2 border-stone-200 bg-stone-100 px-1 font-mono text-[10.5px] text-stone-600">
      {children}
    </kbd>
  );
}

export default DeltaEditor;
